// src/components/Visualization/MetricChart.js
import React, { useState, useMemo } from 'react';
import { 
  ResponsiveContainer, ComposedChart, Line, Bar, Area, XAxis, YAxis, 
  CartesianGrid, Tooltip, Legend, ReferenceLine, Label
} from 'recharts';
import { CalendarClock, Filter, Download, SlidersHorizontal } from 'lucide-react';

/**
 * MetricChart - Componente avançado para gráficos de métricas com múltiplas visualizações
 * 
 * @param {Object} props
 * @param {Array} props.data - Array de dados para o gráfico
 * @param {Array} props.series - Configuração das séries (linhas, barras, áreas)
 * @param {string} props.xAxisDataKey - Chave para o eixo X
 * @param {string} props.title - Título do gráfico
 * @param {string} props.subtitle - Subtítulo do gráfico
 * @param {string} props.height - Altura do gráfico (default: 400px)
 * @param {boolean} props.showControls - Exibe controles (períodos, filtros, download)
 * @param {boolean} props.showLegend - Exibe legenda
 * @param {boolean} props.showGrid - Exibe grid
 * @param {Array} props.periods - Opções de períodos para filtro
 * @param {Array} props.targets - Linhas de referência para metas
 * @param {boolean} props.stacked - Empilha barras ou áreas
 * @param {Object} props.margins - Margens do gráfico
 * @param {Function} props.tooltipFormatter - Função para formatar tooltip
 * @param {Array} props.annotations - Anotações no gráfico (eventos importantes)
 */
const MetricChart = ({
  data = [],
  series = [],
  xAxisDataKey = 'name',
  title = '',
  subtitle = '',
  height = 400,
  showControls = true,
  showLegend = true,
  showGrid = true,
  periods = [
    { label: '7 dias', value: '7d' },
    { label: '30 dias', value: '30d' },
    { label: '90 dias', value: '90d' },
    { label: 'Este ano', value: 'year' }
  ],
  targets = [],
  stacked = false,
  margins = { top: 20, right: 20, left: 20, bottom: 20 },
  tooltipFormatter,
  annotations = []
}) => {
  // Estados para controles
  const [selectedPeriod, setSelectedPeriod] = useState(periods[0]?.value || '7d');
  const [showFilters, setShowFilters] = useState(false);

  // Transformar configuração de séries em elementos do gráfico
  const renderSeries = useMemo(() => {
    return series.map((s, index) => {
      const baseProps = {
        key: `series-${index}`,
        name: s.name,
        dataKey: s.dataKey,
        yAxisId: s.yAxisId || 'left',
        fill: s.color,
        stroke: s.color,
        strokeWidth: s.strokeWidth || 2,
        stackId: stacked ? 'stack1' : undefined,
        opacity: s.opacity !== undefined ? s.opacity : 1
      };

      switch (s.type) {
        case 'bar':
          return <Bar {...baseProps} radius={[4, 4, 0, 0]} />;
        case 'area':
          return <Area {...baseProps} type="monotone" fillOpacity={0.6} />;
        case 'line':
        default:
          return <Line {...baseProps} type="monotone" dot={s.showDots !== false} />;
      }
    });
  }, [series, stacked]);

  // Identificar axes e seus formatos
  const axes = useMemo(() => {
    const leftAxis = series.find(s => s.yAxisId === 'left' || !s.yAxisId);
    const rightAxis = series.find(s => s.yAxisId === 'right');

    return {
      left: leftAxis ? {
        formatter: leftAxis.valueFormatter || (value => value),
        label: leftAxis.axisLabel || '',
        unit: leftAxis.unit || ''
      } : null,
      right: rightAxis ? {
        formatter: rightAxis.valueFormatter || (value => value),
        label: rightAxis.axisLabel || '',
        unit: rightAxis.unit || ''
      } : null
    };
  }, [series]);

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 shadow-lg rounded-lg">
          <p className="text-sm font-semibold text-gray-800 mb-2">{label}</p>
          <div className="space-y-1">
            {payload.map((entry, index) => {
              // Formatar valor conforme configuração da série
              const serie = series.find(s => s.dataKey === entry.dataKey);
              const formattedValue = serie?.valueFormatter 
                ? serie.valueFormatter(entry.value)
                : entry.value;
              
              return (
                <div key={`tooltip-item-${index}`} className="flex items-center">
                  <div 
                    className="h-3 w-3 rounded-full mr-2" 
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-xs font-medium text-gray-600">
                    {entry.name}:
                  </span>
                  <span className="text-xs font-bold ml-1 text-gray-800">
                    {formattedValue}
                    {serie?.unit && ` ${serie.unit}`}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    return null;
  };

  // Função para download dos dados
  const handleDownloadData = () => {
    if (!data.length) return;
    
    // Converter dados para CSV
    const headers = Object.keys(data[0]);
    const csvContent = [
      // Cabeçalhos
      headers.join(','),
      // Linhas de dados
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          return typeof value === 'string' ? `"${value}"` : value;
        }).join(',')
      )
    ].join('\n');
    
    // Criar link de download e clicar automaticamente
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${title.toLowerCase().replace(/\s+/g, '-')}_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="card overflow-hidden">
      {/* Cabeçalho com título e controles */}
      <div className="card-header">
        <div>
          <h2 className="card-title">{title}</h2>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
        
        {showControls && (
          <div className="flex items-center space-x-2">
            {/* Seletor de período */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <span className="px-2 text-gray-500">
                <CalendarClock size={16} />
              </span>
              {periods.map(period => (
                <button
                  key={period.value}
                  className={`text-xs px-3 py-1 rounded-md ${
                    selectedPeriod === period.value
                      ? 'bg-white shadow-sm text-blue-600 font-medium'
                      : 'text-gray-600 hover:bg-gray-200'
                  }`}
                  onClick={() => setSelectedPeriod(period.value)}
                >
                  {period.label}
                </button>
              ))}
            </div>
            
            {/* Botão de filtros */}
            <button 
              className="p-2 rounded-lg text-gray-600 hover:bg-gray-100"
              onClick={() => setShowFilters(!showFilters)}
              title="Filtros avançados"
            >
              <Filter size={18} />
            </button>
            
            {/* Botão para configurações adicionais */}
            <button 
              className="p-2 rounded-lg text-gray-600 hover:bg-gray-100"
              title="Configurações do gráfico"
            >
              <SlidersHorizontal size={18} />
            </button>
            
            {/* Botão de download */}
            <button 
              className="p-2 rounded-lg text-gray-600 hover:bg-gray-100"
              onClick={handleDownloadData}
              title="Download dos dados"
            >
              <Download size={18} />
            </button>
          </div>
        )}
      </div>
      
      {/* Área de filtros (expandível) */}
      {showFilters && (
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex flex-wrap gap-4">
            {/* Aqui você pode adicionar filtros específicos para cada gráfico */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Filtro customizado
              </label>
              <select className="form-select text-sm">
                <option>Todos</option>
              </select>
            </div>
          </div>
        </div>
      )}
      
      {/* Gráfico */}
      <div className="p-0 overflow-hidden" style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart 
            data={data}
            margin={margins}
          >
            {showGrid && <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />}
            
            <XAxis 
              dataKey={xAxisDataKey} 
              tick={{ fontSize: 12, fill: '#6B7280' }}
              axisLine={{ stroke: '#E5E7EB' }}
              tickLine={{ stroke: '#E5E7EB' }}
            />
            
            {axes.left && (
              <YAxis 
                yAxisId="left"
                orientation="left"
                tickFormatter={axes.left.formatter}
                tick={{ fontSize: 12, fill: '#6B7280' }}
                axisLine={{ stroke: '#E5E7EB' }}
                tickLine={{ stroke: '#E5E7EB' }}
              >
                {axes.left.label && (
                  <Label 
                    value={axes.left.label} 
                    position="insideLeft" 
                    angle={-90} 
                    style={{ textAnchor: 'middle', fontSize: 12, fill: '#6B7280' }} 
                  />
                )}
              </YAxis>
            )}
            
            {axes.right && (
              <YAxis 
                yAxisId="right"
                orientation="right"
                tickFormatter={axes.right.formatter}
                tick={{ fontSize: 12, fill: '#6B7280' }}
                axisLine={{ stroke: '#E5E7EB' }}
                tickLine={{ stroke: '#E5E7EB' }}
              >
                {axes.right.label && (
                  <Label 
                    value={axes.right.label} 
                    position="insideRight" 
                    angle={90} 
                    style={{ textAnchor: 'middle', fontSize: 12, fill: '#6B7280' }} 
                  />
                )}
              </YAxis>
            )}
            
            <Tooltip content={<CustomTooltip />} />
            
            {showLegend && (
              <Legend 
                verticalAlign="top" 
                height={36}
                iconSize={10}
                iconType="circle"
                wrapperStyle={{ fontSize: 12, paddingTop: 20 }}
              />
            )}
            
            {/* Renderiza as séries de dados */}
            {renderSeries}
            
            {/* Linhas de metas/referência */}
            {targets.map((target, index) => (
              <ReferenceLine
                key={`target-${index}`}
                y={target.value}
                yAxisId={target.yAxisId || 'left'}
                stroke={target.color || '#FF8042'}
                strokeDasharray="3 3"
                strokeWidth={1.5}
                opacity={0.7}
              >
                {target.label && (
                  <Label 
                    value={target.label} 
                    position="insideTopRight"
                    fill={target.color || '#FF8042'}
                    fontSize={11}
                  />
                )}
              </ReferenceLine>
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default MetricChart;
